import React, { useState } from 'react';
import axios from 'axios';
import { isNil } from 'lodash';

import CardDialog from '~/components/CardDialog';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import Text from '~/components/core/TextComponents/Text';
import type { DocumentRequestDTO } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import DocumentRequestRecipientDialog from './DocumentRequestRecipientDialog';
import DocumentRequestShareDialog from './DocumentRequestShareDialog';
import { TEXTS } from './texts';
import { claimDocumentRequestsUrl } from './useDocumentRequests';

interface RequestDocumentsLinkDialogProps {
  claim: { id: number; organization_id: number };
  onClose: () => void;
}

const RequestDocumentsLinkDialog: React.FC<RequestDocumentsLinkDialogProps> = ({ claim, onClose }) => {
  const [created, setCreated] = useState<DocumentRequestDTO | null>(null);
  const [sharedContactId, setSharedContactId] = useState<number | null>(null);

  if (!isNil(created) && !isNil(sharedContactId)) {
    // The server sends no url when the upload app returned an expiry that CMS's clock already sees as past,
    // so say what to do instead of rendering an empty value.
    if (isNil(created.url)) {
      return (
        <CardDialog
          isDialog
          title={TEXTS.SHARE_DIALOG_TITLE}
          onClose={onClose}
          maxWidth="sm"
          fullWidth
          footerActions={
            <DialogFooterActions secondaryLabel={TEXTS.CLOSE_BUTTON} onClickSecondary={onClose} showPrimary={false} />
          }
        >
          <Text variant={Text.VARIANTS.SM}>{TEXTS.GENERATED_LINK_MISSING}</Text>
        </CardDialog>
      );
    }
    return (
      <DocumentRequestShareDialog
        claim={claim}
        documentRequest={{ id: created.id, url: created.url, expires_at: created.expires_at }}
        contactId={sharedContactId}
        onClose={onClose}
      />
    );
  }

  return (
    <DocumentRequestRecipientDialog
      title={TEXTS.GENERATE_DIALOG_TITLE}
      intro={TEXTS.GENERATE_DIALOG_INTRO}
      primaryLabel={TEXTS.GENERATE_BUTTON}
      onClose={onClose}
      // Minted on a press, never on open: a stray open must not leave a live link that someone has to cancel.
      onSubmit={async (contact) => {
        try {
          const { data } = await axios.post<DocumentRequestDTO>(
            claimDocumentRequestsUrl(claim.organization_id, claim.id)
          );
          setSharedContactId(contact.id);
          setCreated(data);
        } catch (error) {
          await reportAxiosError(error);
        }
      }}
    />
  );
};

export default RequestDocumentsLinkDialog;
